import React, { useEffect, useId, useRef, useState } from 'react';
import NextLink from 'next/link';
import { SvgIcon } from 'components/helpers/SvgIcon';
import styles from './HeaderAccount.module.scss';

export interface HeaderAccountProps {
  manageUrl?: string;
  onLogout?: () => void;
  accountLabel?: string;
  manageLabel?: string;
  logoutLabel?: string;
}

export const HeaderAccount: React.FC<HeaderAccountProps> = ({
  manageUrl,
  onLogout,
  accountLabel = 'My Account',
  manageLabel = 'Manage',
  logoutLabel = 'Log out',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const accountLabelId = useId();
  const accountMenuId = useId();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuItemRefs = useRef<(HTMLElement | null)[]>([]);
  // Tracks which item index to focus when the menu opens (-1 means last item)
  const focusIndexOnOpen = useRef<number>(0);

  // Focus the target menuitem whenever the menu opens (ARIA APG Menu Button pattern).
  // Moves DOM focus inside role="menu" so NVDA switches from Browse Mode to Application Mode.
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        const idx =
          focusIndexOnOpen.current < 0
            ? menuItemRefs.current.length - 1 // ArrowUp → last item
            : 0; // Enter/Space/ArrowDown → first item
        menuItemRefs.current[idx]?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }

    return undefined;
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          buttonRef.current?.focus();
          break;

        case 'ArrowDown':
          e.preventDefault();
          {
            const nextIndex =
              menuItemRefs.current.findIndex(
                (item: HTMLElement | null) => item === document.activeElement
              ) + 1;
            const targetIndex = nextIndex < menuItemRefs.current.length ? nextIndex : 0;
            menuItemRefs.current[targetIndex]?.focus();
          }
          break;

        case 'ArrowUp':
          e.preventDefault();
          {
            const currentIndex = menuItemRefs.current.findIndex(
              (item: HTMLElement | null) => item === document.activeElement
            );
            const targetIndex =
              currentIndex > 0 ? currentIndex - 1 : menuItemRefs.current.length - 1;
            menuItemRefs.current[targetIndex]?.focus();
          }
          break;

        case 'Tab': {
          // currentIndex is -1 when focus is on the trigger button (not a menu item)
          const currentIndex = menuItemRefs.current.findIndex(
            (item: HTMLElement | null) => item === document.activeElement
          );
          if (!e.shiftKey) {
            // Tab (forward): -1+1=0 when on button → enters menu at first item
            const nextIndex = currentIndex + 1;
            if (nextIndex < menuItemRefs.current.length) {
              e.preventDefault();
              menuItemRefs.current[nextIndex]?.focus();
            } else {
              // Past last item — close and let browser continue natural tab order
              setIsOpen(false);
            }
          } else {
            if (currentIndex > 0) {
              // Shift+Tab on non-first item → move to previous item
              e.preventDefault();
              menuItemRefs.current[currentIndex - 1]?.focus();
            } else if (currentIndex === 0) {
              // Shift+Tab on first item → return focus to trigger button
              e.preventDefault();
              setIsOpen(false);
              buttonRef.current?.focus();
            } else {
              // Shift+Tab on button (currentIndex === -1) → close and let browser go backward
              setIsOpen(false);
            }
          }
          break;
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }

    return undefined;
  }, [isOpen]);

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    focusIndexOnOpen.current = 0; // Enter/Space/click → first item
    setIsOpen((prev: boolean) => !prev);
  };

  // ARIA APG: ArrowDown opens menu and focuses first item; ArrowUp opens and focuses last item
  const handleButtonKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusIndexOnOpen.current = 0;
      setIsOpen(true);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusIndexOnOpen.current = -1; // sentinel for last item
      setIsOpen(true);
    }
  };

  const handleLogoutClick = () => {
    setIsOpen(false);
    onLogout?.();
  };

  return (
    <div className={styles.accountDropdown} ref={dropdownRef}>
      <button
        ref={buttonRef}
        className={styles.accountButton}
        onClick={handleToggle}
        onKeyDown={handleButtonKeyDown}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-controls={accountMenuId}
        aria-labelledby={accountLabelId}
        type="button"
      >
        <span className={styles.accountIcon}>
          <SvgIcon icon="user-account" viewBox="0 0 30 30" aria-hidden="true" />
        </span>
        <span className={styles.accountLabel} id={accountLabelId}>
          {accountLabel}
        </span>
        <span
          className={styles.caretIcon}
          style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
        >
          <SvgIcon icon="caret-down" viewBox="0 0 24 24" aria-hidden="true" />
        </span>
      </button>

      {isOpen && (
        <div className={styles.dropdownMenu} id={accountMenuId} role="menu">
          <NextLink
            href={manageUrl || '/user-profile'}
            className={styles.menuItem}
            role="menuitem"
            ref={(el) => {
              menuItemRefs.current[0] = el as HTMLElement | null;
            }}
            onClick={() => setIsOpen(false)}
          >
            <span className={styles.menuItemText}>{manageLabel}</span>
          </NextLink>
          <button
            ref={(el) => {
              menuItemRefs.current[1] = el;
            }}
            className={styles.menuItem}
            role="menuitem"
            onClick={handleLogoutClick}
            type="button"
          >
            <span className={styles.menuItemText}>{logoutLabel}</span>
          </button>
        </div>
      )}
    </div>
  );
};
